import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiGet } from "@/lib/apiClient";
import { ArrowLeft, Building2 } from "lucide-react";

type AccountDetail = {
  id?: string;
  domain: string;
  companyName: string;
  industry?: string | null;
  plan?: string | null;
  status?: string;
  seats?: number | null;
  renewalDate?: string | null;
  timeZone?: string | null;
  mfaRequired?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export function OrganizationDetailPage() {
  const { domain: domainParam } = useParams<{ domain: string }>();
  const domain = domainParam ? decodeURIComponent(domainParam) : "";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [account, setAccount] = useState<AccountDetail | null>(null);

  useEffect(() => {
    if (!domain) return;
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const res = await apiGet<AccountDetail>(`/api/accounts/${encodeURIComponent(domain)}`);
      if (cancelled) return;
      if (!res.ok) {
        setError(res.error || "Could not load organization.");
        setAccount(null);
      } else {
        setError(null);
        setAccount(res.data ?? null);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [domain]);

  return (
    <div className="space-y-6 p-6 sm:p-8">
      <Link
        to="/organization"
        className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-300"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        All organizations
      </Link>

      {loading ? <p className="text-sm text-slate-500">Loading organization…</p> : null}
      {error ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">{error}</div>
      ) : null}

      {account && !error ? (
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-950/40">
          <div className="flex items-start gap-3">
            <Building2 className="h-8 w-8 text-indigo-600" aria-hidden />
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{account.companyName}</h1>
              <p className="font-mono text-sm text-slate-500">{account.domain}</p>
            </div>
          </div>
          <dl className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { label: "Status", value: account.status || "—" },
              { label: "Plan", value: account.plan || "—" },
              { label: "Industry", value: account.industry || "—" },
              { label: "Seats", value: account.seats ?? "—" },
              { label: "Time zone", value: account.timeZone || "—" },
              { label: "MFA required", value: account.mfaRequired ? "Yes" : "No" },
              {
                label: "Renewal",
                value: account.renewalDate ? new Date(account.renewalDate).toLocaleDateString() : "—",
              },
              {
                label: "Created",
                value: account.createdAt ? new Date(account.createdAt).toLocaleString() : "—",
              },
            ].map((row) => (
              <div key={row.label}>
                <dt className="text-xs font-semibold uppercase text-slate-500">{row.label}</dt>
                <dd className="mt-1 text-sm font-medium capitalize text-slate-900 dark:text-white">{String(row.value)}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-6 text-xs text-slate-500">
            Edit organization, users, and domain settings remain on Amplify until this module is fully ported.
          </p>
        </div>
      ) : null}
    </div>
  );
}
